import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from  'rxjs/Observable';
import { tap, catchError } from 'rxjs/operators';
import { forkJoin } from "rxjs/observable/forkJoin";
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import { Subject } from "rxjs/Subject";
import { Category } from '../../interfaces/category.model';
import { Asset } from '../../interfaces/asset.model';
import { Language } from '../../interfaces/language.model';
import { Audience } from '../../interfaces/audience.model';
import { Keyword } from '../../interfaces/keyword.model';
import { Indication } from '../../interfaces/indication.model';
import { AssetType } from '../../interfaces/asset-type.model';
import { SynchroProgress } from '../../interfaces/synchro-progress.model';
import { StorageProvider } from '../../providers/storage/storage';
import { AssetProvider } from '../asset/asset';
import { UserInfo } from '../../interfaces/user-info.model';
import { Action } from '../../interfaces/action.model';
import { ENV } from '@app/env';

import { HttpErrorHandlerProvider, HandleError } from '../http-error-handler/http-error-handler';
import { Platform } from 'ionic-angular';
import { HelperProvider } from '../../providers/helper/helper';

const httpOptions = {
  headers: new HttpHeaders({'Content-Type': 'application/json', 'withCredentials': 'true' })
};

@Injectable()
export class WebserviceProvider {

  private handleError: HandleError;
  user: any;
  categories: Category[];
  assets: Asset[];
  assetsTypes: AssetType[];
  languages: Language[];
  audiences: Audience[];
  indications: Indication[];
  keywords: Keyword[];
  progress: SynchroProgress = {
    InProgress: false,
    Current: 0,
    Total: 0,
    Finished: false,
    Error: false,
    ErrorDetails: null
  };

  wsUrl: string = ENV.wsUrl+'STSService/FFOService.svc/';

  constructor(
    public plt: Platform,
    public http: HttpClient,
    public storageService: StorageProvider,
    public assetService: AssetProvider,
    httpErrorHandler: HttpErrorHandlerProvider,
    public helperService: HelperProvider)
  {
    this.handleError = httpErrorHandler.createHandleError('WebserviceProvider');
  }

  getAllData(): Observable<SynchroProgress>{
    let subject = new Subject<SynchroProgress>();

    this.progress.InProgress = true;

    this.getAllLists().subscribe(data => {
      console.log("ALL LISTS LOADED!");
      this.progress.Current = 0;
      this.progress.Finished = false;
      this.progress.Total = this.assets.length;
      subject.next(this.progress);

      // if(this.assets.length > 0 && this.plt.is('ios')){
      //[VP - 27/01/2021] Use custom function to detect iOS because of ionic bug: https://github.com/ionic-team/ionic-v3/issues/1104
      if(this.assets.length > 0 && this.helperService.isIOS()){
        this.assetService.getAllAssetsFile(subject, this.progress, this.assets);
      } else {
        this.progress.Finished = true;
        subject.next(this.progress);
        subject.complete();
      }
    });

   return subject.asObservable();
  }

  getAllLists(): Observable<any> {
    let observeArray:Observable<any>[] = new Array();
    let res1 = this.getCategories();
    observeArray.push(res1);
    let res2 = this.getAssets();
    observeArray.push(res2);
    let res3 = this.getAssetTypes();
    observeArray.push(res3);
    let res4 = this.getLanguages();
    observeArray.push(res4);
    let res5 = this.getAudiences();
    observeArray.push(res5);
    let res6 = this.getIndications();
    observeArray.push(res6);
    let res7 = this.getKeywords();
    observeArray.push(res7);
    let res8 = this.getNotifications(null);
    observeArray.push(res8);
    return forkJoin(observeArray);
  }

  getLanguages(): Observable<Language[]> {
    return this.http.get<Language[]>(this.wsUrl+'GetLanguages',{ withCredentials: true})
    .pipe(
      tap(languages => this.storageService.setDataStorage("languages", languages)),
      catchError(this.handleError('GetLanguages', []))
    )
  }

  getAudiences(): Observable<Audience[]> {
    return this.http.get<Audience[]>(this.wsUrl+'GetAudiences',{ withCredentials: true})
    .pipe(
      tap(audiences => {
        this.storageService.setDataStorage("audiences", audiences)
        this.storageService.getDataStorage("selectedAudience")
        .then(selectedAudience => {
          if(selectedAudience == null){
            this.storageService.setDataStorage("selectedAudience", audiences[0]);
          }
        });
      }
      ),
      catchError(this.handleError('GetAudiences', []))
    )
  }

  getIndications(): Observable<Indication[]> {
    return this.http.get<Indication[]>(this.wsUrl+'GetIndications',{ withCredentials: true})
    .pipe(
      tap(indications => this.storageService.setDataStorage("indications", indications)),
      catchError(this.handleError('getIndications', []))
    )
  }

  getKeywords(): Observable<Keyword[]> {
    return this.http.get<Keyword[]>(this.wsUrl+'GetKeywords',{ withCredentials: true})
    .pipe(
      tap(keywords => this.storageService.setDataStorage("keywords", keywords)),
      catchError(this.handleError('getKeywords', []))
    )
  }

  getCategories(): Observable<Category[]> {
    return this.http.get<Category[]>(this.wsUrl+'GetCategories',{ withCredentials: true})
    .pipe(
      tap(categories => {
        //this.categories = categories;
        this.storageService.setDataStorage("categories", categories);
      }),
      catchError(this.handleError('getCategories', []))
    )
  }

  getAssetTypes(): Observable<AssetType[]> {
    return this.http.get<AssetType[]>(this.wsUrl+'GetAssetTypes',{ withCredentials: true})
    .pipe(
      tap(assetsTypes => this.storageService.setDataStorage("assetsTypes", assetsTypes)),
      catchError(this.handleError('getAssetTypes', []))
    )
  }

  getAssets(): Observable<Asset[]> {
    return this.http.get<Asset[]>(this.wsUrl+'GetAssets',{ withCredentials: true})
    .pipe(
      tap(assets => {
        this.assets = assets;
        this.storageService.setDataStorage("assets", assets);
      }),
      catchError(this.handleError('getAssets', []))
    )
  }

  getNotifications(lastSyncDate): Observable<any> {
    let syncDateUrl = (lastSyncDate == null) ? "" : "?syncDate="+lastSyncDate;
    return this.http.get<any>(this.wsUrl+'SynchroNotifications'+syncDateUrl, { withCredentials: true})
    .pipe(
      tap(notifications => {
        if(lastSyncDate == null){
          //First synchro with new notification system: set all notfications older than last week as read
          const daysinweek = 7;
          if(notifications.Added.length > 0){
            for (var index = 0; index < notifications.Added.length; index++) {
              let currentNotif = notifications.Added[index];
              // if(this.plt.is('ios')){
              //[VP - 27/01/2021] Use custom function to detect iOS because of ionic bug: https://github.com/ionic-team/ionic-v3/issues/1104
              if(this.helperService.isIOS()){
                const daysBetween = this.helperService.getDaysBetweenDates(currentNotif.NotificationDate);
                if((typeof currentNotif.isRead == 'undefined')) {
                  if (daysBetween > daysinweek){
                    currentNotif.isRead = true;
                  } else {
                    currentNotif.isRead = false;
                  }
                }
              } else {
                currentNotif.isRead = true;
              }
            }
          }
          this.storageService.setDataStorage("notifications", notifications.Added);
        }
      }),
      catchError(this.handleError('getNotifications', null))
    )
  }

  updateUserInfo(userInformation: UserInfo): Observable<boolean> {
    return this.http.post<boolean>(this.wsUrl+'UpdateUserInfo', userInformation, { withCredentials: true })
    .pipe(
      catchError(this.handleError('updateUserInfo', false))
    );
  }

  setUserActions(userActions: Action[]): Observable<boolean> {
    return this.http.post<boolean>(this.wsUrl+'SetUserActions', userActions, { withCredentials: true })
    .pipe(
      catchError(this.handleError('setUserActions', false))
    );
  }

  checkFileExist(fileUrl: string): Observable<any> {
    return this.http.get(fileUrl, { withCredentials: true })
    .pipe(
      catchError(this.handleError('checkFileExist', false))
    );
  }

  /*private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`);
    }
    // return an ErrorObservable with a user-facing error message
    return new ErrorObservable(
      'Something bad happened; please try again later.');
  };*/

}



// WEBPACK FOOTER //
// ./src/providers/webservice/webservice.ts